import { useMemo } from 'react';
import type { Timestamp } from 'firebase/firestore';
import { useCollaboratorByIdQuery, useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import type { Movimentacao, Solicitacao } from '../../../../models';
import { saudacaoPelaHora } from '../../../../models/emailCliente.modelos';
import type { ModeloEmailCliente } from '../../../../models/emailCliente.modelos';
import { usePreCadastroAdmissaoQuery } from '../../../../hooks/data';
import {
  CHAVES_CPF_PRE_CADASTRO,
  CHAVES_ENDERECO_PRE_CADASTRO,
  CHAVES_TELEFONE_PRE_CADASTRO,
  enderecoParaEntrega,
  formatarEndereco,
  respostaPreCadastro,
} from '../../../../utils/enderecoContato';
import { SISTEMA_OPERACIONAL_LABELS } from '../../solicitacaoDetailsPage.definitions';
import { orgaoEmissorCompativelComRg } from '../../../../models/documentoIdentidade';

const CHAVES_RG_PRE_CADASTRO = ['RG'];
const CHAVES_NASCIMENTO_PRE_CADASTRO = ['Data de nascimento', 'Data de Nascimento:'];

function dataBR(data: Timestamp | null | undefined): string {
  return data?.toDate ? data.toDate().toLocaleDateString('pt-BR') : '';
}

/** "1995-05-10" → "10/05/1995"; outros formatos ficam como vieram. */
function dataTextoBR(data: string | null | undefined): string {
  const iso = data?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : (data ?? '');
}

/**
 * Valores das variáveis do e-mail para o Cliente (ver VARIAVEIS_EMAIL_CLIENTE): cadastro do
 * colaborador ou, em Admissão ainda sem vínculo, o pré-cadastro; Movimentação daquele Cliente
 * (SO, datas, gestor Raro); e o bloco "Dados para o cliente" da abertura. Só o SI usa — mesmo
 * acesso a collaborators_private do cabeçalho (ColaboradorCabecalhoInfo).
 */
export function useValoresEmailCliente(
  solicitacao: Solicitacao,
  movimentacoes: Movimentacao[],
  modelo: ModeloEmailCliente | null
): { valores: Record<string, string>; avisos: Record<string, string> } {
  const pendente = solicitacao.colaboradorId == null ? solicitacao.colaboradorPendente : null;
  const { data: colaborador } = useCollaboratorByIdQuery(modelo ? (solicitacao.colaboradorId ?? undefined) : undefined);
  const { data: colaboradoresPublicos = [] } = useCollaboratorsPublicQuery();
  // Também com colaborador já vinculado: é a única fonte que diz explicitamente "RG".
  const { data: preCadastro } = usePreCadastroAdmissaoQuery(modelo ? solicitacao.preCadastroId : null);

  return useMemo(() => {
    if (!modelo) return { valores: {}, avisos: {} };
    const publico = colaboradoresPublicos.find((c) => c.id === solicitacao.colaboradorId) ?? null;
    // Movimentação deste Cliente no sentido do e-mail (entrada = envio, saída = devolução).
    const ativas = movimentacoes.filter((m) => m.statusMovimentacao !== 'cancelada');
    const doCliente = ativas.filter((m) => (m.clienteId ?? solicitacao.clienteId) === modelo.clienteId);
    const fluxo = modelo.momento === 'entrada' ? 'envio' : 'devolucao';
    const movimentacao = doCliente.find((m) => m.tipoFluxo === fluxo) ?? doCliente[0] ?? ativas[0] ?? null;
    const so = movimentacao?.equipamentosIntake.find((item) => item.sistemaOperacional)?.sistemaOperacional;
    const pre = (chaves: string[]) => respostaPreCadastro(preCadastro, chaves) ?? '';
    const dadosAbertura = solicitacao.dadosCliente ?? {};

    // RG: o "Nº do Documento de Identificação" do perfil pode ser CNH, RNE etc. — só vale quando
    // o órgão emissor é de RG. Depois, o pré-cadastro (pergunta "RG" mesmo) e o que quem abriu
    // informou (a abertura pede quando o Digital não tem, ver verificarDadosEmailCliente).
    const documentoPerfil = colaborador?.identityDocument;
    const rgPerfil =
      documentoPerfil?.number && orgaoEmissorCompativelComRg(documentoPerfil.issuer, documentoPerfil.uf)
        ? documentoPerfil.number
        : '';
    const rg = rgPerfil || pre(CHAVES_RG_PRE_CADASTRO) || dadosAbertura.rg || '';
    const avisos: Record<string, string> = {};
    if (!rg && documentoPerfil?.number) {
      const orgao = [documentoPerfil.issuer, documentoPerfil.uf].filter(Boolean).join('/') || 'não informado';
      avisos.rg = `O documento do perfil não parece RG (órgão ${orgao}) — não foi usado.`;
    }

    const valores = {
      ...dadosAbertura,
      saudacao: saudacaoPelaHora(),
      nome: pendente?.nome ?? colaborador?.name ?? solicitacao.colaboradorNome,
      funcao: (pendente ? pendente.positionName : (publico?.positionName ?? colaborador?.positionName)) ?? '',
      email_raro: pendente ? '' : (colaborador?.email ?? solicitacao.colaboradorEmail),
      // Admissão sem vínculo: o e-mail informado na abertura é o pessoal (é por ele que o
      // pré-cadastro é vinculado).
      email_pessoal: pendente ? (preCadastro?.emailPessoal ?? pendente.emailInformado) : (colaborador?.personalEmail ?? ''),
      telefone: pendente ? pre(CHAVES_TELEFONE_PRE_CADASTRO) : (colaborador?.phone ?? ''),
      cpf: pendente ? pre(CHAVES_CPF_PRE_CADASTRO) : (colaborador?.cpf ?? ''),
      rg,
      data_nascimento: pendente ? pre(CHAVES_NASCIMENTO_PRE_CADASTRO) : dataTextoBR(colaborador?.birthDate),
      endereco: pendente
        ? pre(CHAVES_ENDERECO_PRE_CADASTRO)
        : (formatarEndereco(enderecoParaEntrega(colaborador?.addresses)) ?? ''),
      so: so ? SISTEMA_OPERACIONAL_LABELS[so] : '',
      cliente: modelo.clienteNome,
      // Pedido na abertura; Solicitações antigas (sem o campo) caem no responsável da alocação.
      gestor_raro:
        dadosAbertura.gestor_raro || movimentacao?.alocacaoResponsavelNome || solicitacao.alocacaoResponsavelNome || '',
      data_inicio: modelo.momento === 'entrada' ? dataBR(movimentacao?.dataPrevista) : '',
      data_termino:
        modelo.momento === 'saida' ? dataBR(movimentacao?.dataPrevista) || dataTextoBR(colaborador?.endDate) : '',
    };
    return { valores, avisos };
  }, [modelo, solicitacao, movimentacoes, colaborador, colaboradoresPublicos, preCadastro, pendente]);
}
